/* Set logging on the Today tab: what an empty row stands for, what Done
 * saves, the load step of the steppers and the add/remove/undo edits. Pure
 * (state in, new state out) so the rules are pinned by setLog.test.js. */
import MUSCLE_MAP from './logic/muscleMap.json';
import { resolveMuscles } from './logic/muscles';
import { learnIncrement } from './logic/progression';

const isNum = v => typeof v === 'number' && Number.isFinite(v);
const isObj = v => !!v && typeof v === 'object' && !Array.isArray(v);

/* A value Mo typed (old data may hold numeric strings), else null. */
function typed(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * The ghost values of row `index`: shown greyed out, and saved by Done when
 * a field is empty. Today's target (logic/progression targetsFor: a Claude
 * prescription, a program block or the plan's progression rule) comes first.
 * A straight-set target follows a weight typed on the row above, because Mo
 * changed the load on purpose; a ramp (5/3/1) keeps its own loads.
 * Without a target row: weight row 0 = last session's first set, later rows
 * = the row above as typed, else its own ghost, because most sets repeat the
 * weight before them; reps = last session's same row, else the plan's reps.
 * @param {{sets?: Array, prevSets?: Array, planReps?: number, target?: Array}} ctx
 *   sets = today's logs for the exercise, prevSets = last session's done
 *   sets, target = targetsFor(...).sets ({weight, reps, amrap})
 * @param {number} index
 * @returns {{weight: number|null, reps: number|null, amrap?: true}} amrap
 *   only on an AMRAP target row (reps is then the minimum)
 */
export function placeholderFor(ctx, index) {
  const sets = Array.isArray(ctx?.sets) ? ctx.sets : [];
  const prev = Array.isArray(ctx?.prevSets) ? ctx.prevSets : [];
  const target = Array.isArray(ctx?.target) ? ctx.target : [];
  let weight = null;
  for (let i = 0; i <= index; i += 1) {
    const tw = isObj(target[i]) ? typed(target[i].weight) : null;
    const above = i > 0 ? typed(sets[i - 1]?.weight) : null;
    if (tw === null) weight = i === 0 ? typed(prev[0]?.weight) : (above ?? weight ?? typed(prev[i]?.weight));
    else weight = i > 0 && tw === typed(target[i - 1]?.weight) ? (above ?? weight) : tw;
  }
  const row = isObj(target[index]) ? target[index] : null;
  const targetReps = row ? typed(row.reps) : null;
  const lastReps = typed(prev[index]?.reps);
  const planReps = typed(ctx?.planReps);
  let reps = null;
  if (targetReps !== null && targetReps > 0) reps = targetReps;
  else if (lastReps !== null && lastReps > 0) reps = lastReps;
  else if (planReps !== null && planReps > 0) reps = planReps;
  return row && row.amrap === true ? { weight, reps, amrap: true } : { weight, reps };
}

/**
 * What Done saves for a row: typed values win, empty fields take the ghost.
 * @returns {{weight: number|null, reps: number|null, autoWeight: boolean, autoReps: boolean}}
 *   null when neither exists; auto* = the value came from the ghost
 */
export function resolveDone(setData, ghost) {
  const w = typed(setData?.weight);
  const r = typed(setData?.reps);
  return {
    weight: w ?? ghost?.weight ?? null,
    reps: r ?? ghost?.reps ?? null,
    autoWeight: w === null && isNum(ghost?.weight),
    autoReps: r === null && isNum(ghost?.reps),
  };
}

/* Mo touching a set makes it his: Claude's `by` mark goes (data model). */
function own(set) {
  const { by, ...rest } = isObj(set) ? set : {};
  return rest;
}

/**
 * A set ticked done. Typed values are kept; `weight`/`reps` only fill empty
 * fields. `now` (epoch ms) becomes doneAt and is only passed on the live day.
 */
export function tickSet(set, { weight = null, reps = null, now = null } = {}) {
  const { doneAt, ...rest } = own(set);
  const out = { ...rest, done: true };
  if (typed(rest.weight) === null && weight !== null) out.weight = weight;
  if (typed(rest.reps) === null && reps !== null) out.reps = reps;
  if (isNum(now)) out.doneAt = now;
  return out;
}

/* Unticked: the time goes with it. Deleted, never set to undefined, because
 * the sync hash would read an undefined key as null and stop matching. */
export function untickSet(set) {
  const { doneAt, ...rest } = own(set);
  return { ...rest, done: false };
}

/* rir 0-5 (5 = "4+"); null removes it. */
export function withRir(set, rir) {
  const { rir: _old, ...rest } = own(set);
  return isNum(rir) ? { ...rest, rir } : rest;
}

export function patchSet(set, patch) {
  return { ...own(set), ...patch };
}

export function rirLabel(rir) {
  if (!isNum(rir)) return '';
  return rir >= 5 ? '4+' : String(rir);
}

/** Replace logs[key][name][index] with `set`, padding with {} rows. */
export function writeSet(state, key, name, index, set) {
  const log = { ...(state.logs?.[key] || {}) };
  const sets = [...(log[name] || [])];
  while (sets.length <= index) sets.push({});
  sets[index] = set;
  log[name] = sets;
  return { ...state, logs: { ...state.logs, [key]: log } };
}

export function readSet(state, key, name, index) {
  return state.logs?.[key]?.[name]?.[index] || {};
}

/** Planned rows for the day: the day's set count, else the card's sets (the
 * plan's, or a program's or prescription's set count). */
export function plannedCount(state, key, ex) {
  if (!ex.planned) return 0;
  const n = state.setCounts?.[key]?.[ex.id];
  return isNum(n) ? n : (Number(ex.sets) || 0);
}

function withCount(state, key, exId, n) {
  const setCounts = { ...(state.setCounts || {}) };
  setCounts[key] = { ...(setCounts[key] || {}), [exId]: n };
  return { ...state, setCounts };
}

/**
 * Add a row. Planned exercises raise the day's set count above every row
 * shown (rows = max(count, logged sets), so pushing an empty set alone left
 * the new row invisible while fewer sets were logged than planned); ad-hoc
 * exercises get an empty set.
 */
export function addSetTo(state, key, ex) {
  const sets = state.logs?.[key]?.[ex.name] || [];
  if (ex.planned) return withCount(state, key, ex.id, Math.max(plannedCount(state, key, ex), sets.length) + 1);
  const log = { ...(state.logs?.[key] || {}) };
  log[ex.name] = [...sets, {}];
  return { ...state, logs: { ...state.logs, [key]: log } };
}

/**
 * Remove row `index`. A planned exercise also loses a planned row when the
 * index is inside the planned range, or the row would come back empty.
 */
export function removeSetFrom(state, key, ex, index) {
  let next = state;
  const sets = state.logs?.[key]?.[ex.name];
  if (Array.isArray(sets) && index < sets.length) {
    const log = { ...(state.logs[key] || {}) };
    log[ex.name] = sets.filter((_, i) => i !== index);
    next = { ...state, logs: { ...state.logs, [key]: log } };
  }
  const count = plannedCount(state, key, ex);
  if (ex.planned && index < count) next = withCount(next, key, ex.id, Math.max(0, count - 1));
  return next;
}

/* Skip an exercise for one day: its sets go, and a planned one gets 0 sets
 * that day (the plan itself is untouched). snapshotExercise/restoreExercise
 * undo it. */
export function skipExercise(state, key, ex) {
  let next = state;
  const log = state.logs?.[key];
  if (isObj(log) && Object.prototype.hasOwnProperty.call(log, ex.name)) {
    const { [ex.name]: _gone, ...rest } = log;
    next = { ...state, logs: { ...state.logs, [key]: rest } };
  }
  return ex.planned ? withCount(next, key, ex.id, 0) : next;
}

/* A planned exercise skipped that day: 0 sets and nothing logged. */
export function isSkipped(state, key, ex) {
  if (!ex.planned || plannedCount(state, key, ex) !== 0) return false;
  const sets = state.logs?.[key]?.[ex.name];
  return !Array.isArray(sets) || sets.length === 0;
}

/* Back to the plan's set count for that day. */
export function unskipExercise(state, key, ex) {
  const day = { ...(state.setCounts?.[key] || {}) };
  if (!Object.prototype.hasOwnProperty.call(day, ex.id)) return state;
  delete day[ex.id];
  const setCounts = { ...(state.setCounts || {}) };
  if (Object.keys(day).length) setCounts[key] = day;
  else delete setCounts[key];
  return { ...state, setCounts };
}

/** What undo needs: the exercise's sets and day set count before an edit. */
export function snapshotExercise(state, key, ex) {
  const log = state.logs?.[key];
  const counts = state.setCounts?.[key];
  return {
    key, name: ex.name, exId: ex.id,
    hasSets: isObj(log) && Object.prototype.hasOwnProperty.call(log, ex.name),
    sets: log?.[ex.name],
    hasCount: isObj(counts) && Object.prototype.hasOwnProperty.call(counts, ex.id),
    count: counts?.[ex.id],
  };
}

/**
 * Put back logs[key][name] and setCounts[key][exId] from a snapshot. Only
 * those two paths, so anything a sync merged in meanwhile stays.
 */
export function restoreExercise(state, snap) {
  const { key, name, exId } = snap;
  const log = { ...(state.logs?.[key] || {}) };
  if (snap.hasSets) log[name] = snap.sets;
  else delete log[name];
  const logs = { ...state.logs };
  if (Object.keys(log).length) logs[key] = log;
  else delete logs[key];

  const day = { ...(state.setCounts?.[key] || {}) };
  if (snap.hasCount) day[exId] = snap.count;
  else delete day[exId];
  const setCounts = { ...(state.setCounts || {}) };
  if (Object.keys(day).length) setCounts[key] = day;
  else delete setCounts[key];
  return { ...state, logs, setCounts };
}

/** First row that is not done, or -1. */
export function firstOpenRow(sets, rows) {
  for (let i = 0; i < rows; i += 1) if (!sets?.[i]?.done) return i;
  return -1;
}

/**
 * The exercise to open after `currentId` is finished: the next unfinished
 * one below it, else the first unfinished one above it.
 * @param {Array<{id: string, done: boolean}>} list in display order
 * @returns {string|null}
 */
export function nextUnfinished(list, currentId) {
  const at = list.findIndex(x => x.id === currentId);
  const order = at < 0 ? list : [...list.slice(at + 1), ...list.slice(0, at)];
  const hit = order.find(x => !x.done);
  return hit ? hit.id : null;
}

/**
 * The set Mo lifts after ticking row `index` of rows[at] (for the rest
 * timer's GO line): the next open row of that exercise, else the first open
 * row of the next unfinished exercise (below, then above, as nextUnfinished).
 * Its numbers are what Done would save there: typed, else the ghost.
 * @param {Array<{ex: {name: string, planned: boolean}, sets: Array, totalRows: number,
 *   targetSets: number}>} rows the Today cards in display order
 * @param {number} at index of the ticked card in rows
 * @param {{weight: number|null, reps: number|null}} saved what the tick saved
 * @param {(row: object, i: number) => {weight, reps}} ghostOf the row ghost
 * @returns {{exercise: string, n: number, of: number, weight: number|null, reps: number|null}|null}
 */
export function upNextSet(rows, at, index, saved, ghostOf) {
  const row = rows[at];
  if (!row) return null;
  const sets = [...(row.sets || [])];
  while (sets.length <= index) sets.push({});
  sets[index] = { ...sets[index], weight: saved.weight, reps: saved.reps, done: true };
  const order = [{ ...row, sets }, ...rows.slice(at + 1), ...rows.slice(0, at)];
  for (const r of order) {
    const i = firstOpenRow(r.sets, r.totalRows);
    if (i < 0) continue;
    const v = resolveDone(r.sets[i], ghostOf(r, i));
    return {
      exercise: r.ex.name, n: i + 1, of: r.ex.planned ? Math.max(r.targetSets, i + 1) : r.totalRows,
      weight: v.weight, reps: v.reps,
    };
  }
  return null;
}

/* Load step by equipment when there is no history to learn it from. */
const EQUIPMENT_STEP = { barbell: 2.5, dumbbell: 2, machine: 5, cable: 5, plate: 2.5, kettlebell: 4, bodyweight: 2.5 };
const ENTRIES = [...MUSCLE_MAP.library, ...MUSCLE_MAP.extras];

function equipmentOf(name) {
  const match = resolveMuscles(name)?.match;
  const entry = match ? ENTRIES.find(e => e.name === match) : null;
  if (entry) return entry.equipment;
  const n = ` ${String(name || '').toLowerCase()} `;
  if (/\b(dumbbells?|db)\b/.test(n)) return 'dumbbell';
  if (/\b(cable|machine|smith|pulldown|pushdown|pec deck)\b/.test(n)) return 'machine';
  if (/\b(pull-?ups?|chin-?ups?|push-?ups?|dips?|plank)\b/.test(n) && !/assisted/.test(n)) return 'bodyweight';
  return null;
}

/** Default kg step for an exercise's equipment (2.5 when unknown). */
export function equipmentStep(name) {
  return EQUIPMENT_STEP[equipmentOf(name)] || 2.5;
}

/* Bodyweight exercises may be ticked without a weight. */
export function isBodyweight(name) {
  return equipmentOf(name) === 'bodyweight';
}

/**
 * kg step of the steppers: the plan exercise's `increment`, else the step
 * learned from Mo's own history, else the equipment default.
 */
export function loadStep(name, planExercise, logs, exerciseMeta) {
  if (isNum(planExercise?.increment) && planExercise.increment > 0) return planExercise.increment;
  return learnIncrement(logs, name, equipmentStep(name), { exerciseMeta });
}

/** base + delta, rounded to 0.01 and never below `min`. */
export function stepValue(base, delta, min = 0) {
  const b = isNum(base) ? base : 0;
  return Math.max(min, Math.round((b + delta) * 100) / 100);
}
